import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Tool } from "@cohesive/api-entities";
import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import React, { useMemo, useState } from "react";
import { IntegrationIcon } from "../../IntegrationIcon/IntegrationIcon";
import {
  BaseEntityCardContextMenu,
  BaseEntityCardProcesses,
  ContextMenuAction,
  Process,
} from "../BaseCard";

type ToolCardProps = Tool & {
  onClick?: () => void;
  onDoubleClick?: () => void;
  actions?: ContextMenuAction[];
  processes?: Process[];
  className?: string;
  style?: React.CSSProperties;
};

// Avatar component for ToolCard
export function ToolCardAvatar({
  platform_name,
  platform_image_url,
  className,
  style,
}: {
  platform_name?: string;
  platform_image_url?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <IntegrationIcon
      platform={platform_name}
      imageURL={platform_image_url}
      className={cn("h-10 w-10 flex-shrink-0", className)}
      style={style}
    />
  );
}

export const ToolCard: React.FC<ToolCardProps> = ({
  display_name,
  name,
  description,
  platform_name,
  platform_image_url,
  parameters,
  onClick,
  onDoubleClick,
  actions,
  processes,
  className,
  style,
}) => {
  const [showParameters, setShowParameters] = useState(false);

  // Format the parameters for display
  const formattedParameters = useMemo(() => {
    if (
      !parameters ||
      !parameters.properties ||
      Object.keys(parameters.properties).length === 0
    ) {
      return null;
    }

    try {
      return JSON.stringify(parameters, null, 2);
    } catch {
      return "Invalid parameters data";
    }
  }, [parameters]);

  return (
    <div className="w-full">
      <Card
        className={cn("w-full transition-colors", className, {
          "cursor-pointer": typeof onClick === "function",
        })}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        style={style}
      >
        <CardContent className="p-3">
          <div className="space-y-3">
            {/* Header Row with Icon and Title */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <ToolCardAvatar
                  platform_name={platform_name}
                  platform_image_url={platform_image_url}
                  className="h-5 w-5"
                />
                <div className="font-semibold truncate">
                  <span>{display_name || name || "Unnamed Tool"}</span>
                </div>
              </div>
              {actions && actions.length > 0 && (
                <BaseEntityCardContextMenu actions={actions} />
              )}
            </div>

            {/* Description */}
            {description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {description}
              </p>
            )}

            {/* Collapsible Parameters Section */}
            {formattedParameters && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 p-0 text-xs text-muted-foreground hover:text-foreground -ml-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowParameters(!showParameters);
                    }}
                  >
                    {showParameters ? (
                      <ChevronDown className="h-3 w-3 mr-1" />
                    ) : (
                      <ChevronRight className="h-3 w-3 mr-1" />
                    )}
                    <Wrench className="h-3 w-3 mr-1" />
                    View parameters
                  </Button>
                </div>

                {showParameters && (
                  <div className="mt-1">
                    <pre className="text-xs bg-muted/50 p-2 rounded border overflow-x-auto max-w-full max-h-48 overflow-y-auto">
                      <code className="text-muted-foreground font-mono break-all whitespace-pre-wrap">
                        {formattedParameters}
                      </code>
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {processes && processes.length > 0 && (
        <BaseEntityCardProcesses processes={processes} />
      )}
    </div>
  );
};

// Skeleton component for loading states
export const ToolCardSkeleton: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className, style }) => {
  return (
    <Card className={cn("w-full transition-colors", className)} style={style}>
      <CardContent className="p-3">
        <div className="space-y-3">
          {/* Header skeleton */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-5 rounded-md" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-6 w-6 rounded-md" />
          </div>

          {/* Description skeleton */}
          <div className="space-y-1">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>

          {/* Parameters button skeleton */}
          <Skeleton className="h-5 w-24" />
        </div>
      </CardContent>
    </Card>
  );
};
